<template>
  <div>
    <div class="col-lg-12">
      <div class="panel-group">
        <div class="panel white">
          <div class="panel-heading">
            <h4 :class="[expandSettings ? 'expanded-opt' : '', 'panel-title']">
              <a
                data-test="user-settings-panel-toggle"
                data-toggle="collapse"
                :href="'#settings_' + userData.id"
                @click="toggleSettings"
                ><i
                  :class="[
                    expandSettings ? 'fa-angle-up' : 'fa-angle-down',
                    'fas',
                  ]"
                />
                {{ trans("center.userSettings") }}</a
              >
            </h4>
          </div>
          <div :id="'settings_' + userData.id" class="panel-collapse collapse">
            <div class="panel-body">
              <user-settings
                v-if="pendingRolesLoaded"
                :user="user"
                :errors="errors"
                :user-roles="pendingRoles"
                :view-as-region-admin="viewAsRegionAdmin"
                :is-mobile-only="centerData?.is_mobile_only ?? false"
                @user-settings-changes="$emit('userSettingsChanges')"
                @connected-centers-editing="connectedCentersEditing = $event"
              />

              <div class="buttons-group mb-2 col-md-12 text-right">
                <span class="float-left mr-1"
                  ><i class="far fa-asterisk" />
                  {{ trans("center.requiredField") }}</span
                >
                <button
                  v-if="!user.blocked"
                  data-test="user-settings-send-reset-btn"
                  type="button"
                  class="btn btn-secondary mb-1"
                  :disabled="connectedCentersEditing || sendingPasswordReset"
                  @click="sendPasswordReset"
                >
                  <span v-if="user.is_verified">{{
                    trans("center.sendPasswordReset")
                  }}</span>
                  <span v-if="!user.is_verified">{{
                    trans("center.resendVerificationEmail")
                  }}</span>
                </button>
                <button
                  data-test="user-settings-save-btn"
                  type="button"
                  class="btn btn-primary mb-1"
                  :disabled="!isValid || loading || connectedCentersEditing"
                  :title="
                    isValid ? '' : trans('examination.requiredFieldMissing')
                  "
                  @click="saveChanges"
                >
                  <span>{{ trans("center.saveChanges") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="panel-group">
        <div class="panel white">
          <div class="panel-heading">
            <h4 :class="[expandProducts ? 'expanded-opt' : '', 'panel-title']">
              <a
                data-test="user-products-panel-toggle"
                data-toggle="collapse"
                :href="'#products_' + userData.id"
                @click="toggleProducts"
                ><i
                  :class="[
                    expandProducts ? 'fa-angle-up' : 'fa-angle-down',
                    'fas',
                  ]"
                />
                {{ trans("center.productsAndNotifications") }}</a
              >
            </h4>
          </div>
          <div :id="'products_' + userData.id" class="panel-collapse collapse">
            <div class="panel-body">
              <user-products
                ref="userProducts"
                :user-id="userData.id"
                :source="source"
                :context="context"
                :can-view-clinical-data="canViewClinicalData"
              />
              <div class="buttons-group text-right">
                <button
                  data-test="user-products-save-btn"
                  type="button"
                  class="btn btn-primary"
                  :disabled="!isValid || loading"
                  :title="
                    isValid ? '' : trans('examination.requiredFieldMissing')
                  "
                  @click="saveChanges"
                >
                  <span>{{ trans("center.saveChanges") }}</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserSettings from "./UserSettings.vue";
import UserProducts from "../../Admin/Users/User/UserProducts.vue";

import Notifications from "../../Utils/notifications";
import { practitionerHttpService } from "../../../app/practitioner-portal/store/practitioner-http-service";

export default {
  components: {
    UserSettings,
    UserProducts,
  },
  props: {
    userData: Object,
    dataUrl: String,
    centerData: Object,
    viewAsRegionAdmin: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["userSettingsChanges"],
  data() {
    return {
      loading: false,
      sendingPasswordReset: false,
      user: {},
      errors: [],
      connectedCentersEditing: false,
      expandSettings: false,
      expandProducts: false,
      roles: [],
      pendingRoles: {
        consultant: false,
        senior_consultant: false,
        user: false,
        user_without_clinical_view: false,
        center_admin: false,
        region_admin: false,
        external_resource: false,
        region_external_resource: false,
        admin: false,
        case_admin: false,
        consultation_photographer: false,
        consultation_creator: false,
      },
      pendingRolesLoaded: false,
      originalBlocked: false,
    };
  },
  computed: {
    canViewClinicalData() {
      return !this.pendingRoles.user_without_clinical_view;
    },
    isValid: function () {
      if (
        !this.user.culture ||
        !this.user.email ||
        !this.user.firstname ||
        !this.user.lastname ||
        (this.user.id && !this.user.username)
      ) {
        return false;
      } else {
        return true;
      }
    },
    source: function () {
      return this.viewAsRegionAdmin ? "region" : "center";
    },
    context: function () {
      return this.viewAsRegionAdmin
        ? this.centerData.region_id
        : this.centerData.id;
    },
  },
  watch: {
    userData: [
      {
        handler: "getUserData",
      },
    ],
    "pendingRoles.consultation_photographer": function (newValue) {
      let instance = this;

      if (newValue === true) {
        instance.pendingRoles.consultation_photographer = true;
        instance.pendingRoles.consultation_creator = false;
      } else if (newValue === false) {
        instance.pendingRoles.consultation_photographer = false;
        instance.pendingRoles.consultation_creator = true;
      }
    },
  },
  mounted: function () {
    this.loadRoles();
  },
  methods: {
    loadRoles: function () {
      this.user = { ...this.userData };
      // Declare dynamically updating variable and assign role to true if the role is in the assigned user roles list
      this.pendingRoles = this.mapRolesToBoolean(this.user.roles);
      this.originalBlocked = !!this.user.blocked;
      this.pendingRolesLoaded = true;
    },
    mapRolesToBoolean(roles) {
      return roles.reduce((acc, role) => {
        acc[role.name] = true;
        return acc;
      }, {});
    },
    saveChanges: function () {
      // Show a confirmation dialog when user.blocked transitions from false to true.

      let currentRoles = this.mapRolesToBoolean(this.user.roles);
      let noPendingExternalResourceRole =
        !this.pendingRoles.external_resource &&
        !this.pendingRoles.region_external_resource;

      let message = [
        this.user.blocked && !this.originalBlocked
          ? this.trans("general.modalBlockUserMessage")
          : false,
        noPendingExternalResourceRole && currentRoles.external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalExternalResourceRemoveMessage",
            )
          : false,
        noPendingExternalResourceRole && currentRoles.region_external_resource
          ? this.getExternalResourceModalTranslation(
              "general.modalRegionExternalResourceRemoveMessage",
            )
          : false,
      ].filter(Boolean);

      if (message.length > 0) {
        this.$vueAlert
          .confirm(undefined, undefined, undefined, {
            html: message.map((msg) => {
              return msg;
            })[0],
            confirmButtonText: this.trans("general.modalConfirmButton"),
            cancelButtonText: this.trans("general.modalCancelButton"),
            onOpen: (popup) => {
              popup
                .querySelector(".swal2-confirm")
                ?.setAttribute("data-test", "user-save-confirm");
              popup
                .querySelector(".swal2-cancel")
                ?.setAttribute("data-test", "user-save-cancel");
            },
          })
          .then(() => {
            // If confirmed, proceed with saving.
            this.submitForm();
          })
          .catch(() => {
            // If canceled, abort the save.
            return false;
          });
      } else {
        this.submitForm();
      }
    },
    submitForm() {
      this.errors = [];
      this.loading = true;

      this.user.userRoles = this.pendingRoles;
      this.user.center = this.centerData.id;
      this.user.settings_products = this.$refs.userProducts.getProducts();

      practitionerHttpService
        .post(this.dataUrl + "/users/" + this.user.id, this.user)
        .then(() => {
          this.$emit("userSettingsChanges");
          this.loading = false;
          Notifications.saveSuccess();
        })
        .catch((error) => {
          this.errors = error.response.data;
          this.loading = false;
          this.userErrorMessage(error);
        });
    },
    sendPasswordReset: function () {
      if (this.sendingPasswordReset) {
        return;
      }
      this.sendingPasswordReset = true;
      practitionerHttpService
        .post(this.dataUrl + "/users/password/reset/" + this.userData.id)
        .then((response) => {
          this.sendingPasswordReset = false;
          if (response.data?.success !== 1) {
            Notifications.warn();
            return;
          }
          Notifications.saveSuccessCustomText(
            "general.notificationPasswordResetSent",
          );
        })
        .catch((error) => {
          this.sendingPasswordReset = false;
          this.userErrorMessage(error);
        });
    },
    getUserData() {
      this.loadRoles();
    },
    toggleSettings: function () {
      this.expandSettings = !this.expandSettings;
    },
    toggleProducts: function () {
      this.expandProducts = !this.expandProducts;
    },
    getExternalResourceModalTranslation(translationKey) {
      return this.trans(translationKey, {
        userGlue: this.trans("general.modalExternalResourceRemoveUserGlue"),
        theirGlue: this.trans("general.modalExternalResourceRemoveTheirGlue"),
      });
    },
  },
};
</script>

<style lang="scss" scoped>
@use "/resources/assets/sass/abstracts/variables";

.far.fa-angle-down,
.far.fa-angle-up {
  font-size: large;
  vertical-align: middle;
}
.hiddenRow {
  padding: 0 !important;
  background: #f5f5f5;
  padding: 1rem;
}
.white {
  background: white;
}
.expanded-opt,
.expanded-opt.panel-title a {
  color: variables.$dermicusBlue;
}

.panel-title > a {
  display: block;
  color: #626773;
  &:hover {
    color: variables.$mellanBlue;
  }
}
.panel-group {
  margin: 1rem;
}
.panel-body {
  border: none;
}
.panel-group .panel-heading + .panel-collapse > .panel-body,
.panel-group > .white > div.panel-heading {
  border: none !important;
}

.buttons-group {
  margin: 1rem;
  .btn {
    margin-right: 0.3rem;
  }
}
</style>
